import { useQuery } from '@apollo/client/react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  BodyText,
  Flex,
  Heading,
  InlineLink,
  RadioGroup,
  RadioTile,
  TextInput,
} from '@utilitywarehouse/hearth-react';
import { TickSmallIcon } from '@utilitywarehouse/hearth-react-icons';
import { clsx } from 'clsx';
import { FormikProps, useFormikContext } from 'formik';
import { ReactNode, useEffect, useRef } from 'react';

import { MaskedInput } from '@components/form/MaskedInput';
import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { AddressForm } from '@features/Address/AddressPicker';
import { formatAddress } from '@features/Address/formatAddress';
import { getAddressFieldValues } from '@features/Address/utils';
import { BundleBasketFragment } from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { useIsInView } from '@hooks/useIsInView';
import { useSyncState } from '@hooks/useSyncState';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { BooleanSelection } from '@src/types/index';
import type { DirectDebitFormValues as FormValues } from '@src/types/yourDetails';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupCreditCheckNoticeDisplayedOnce } from '@tracking/trackingEvents';
import { getBundleBasket } from '@utils/queries/getBasket';

import '../styles.scss';
import './styles.scss';

const GuaranteeListItem = ({ children }: { children: ReactNode }) => {
  return (
    <Flex spacing="sm" asChild>
      <li>
        <TickSmallIcon />
        <BodyText>{children}</BodyText>
      </li>
    </Flex>
  );
};

const DirectDebit = () => {
  const noticeRef = useRef<HTMLDivElement>(null);
  const isInView = useIsInView(noticeRef);
  const trackCreditCheckNoticeDisplayed =
    useTrackSignupCreditCheckNoticeDisplayedOnce();
  const { trackingContexts } = useTrackingContext();
  const [accordionExpanded, setAccordionExpanded] = useSyncState<
    string | undefined
  >('dd-guarantee', undefined);

  useEffect(() => {
    if (isInView) {
      trackCreditCheckNoticeDisplayed({}, { contexts: trackingContexts });
    }
  }, [isInView, trackCreditCheckNoticeDisplayed, trackingContexts]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitCount,
    setFieldValue,
  }: FormikProps<FormValues> = useFormikContext();
  const { basketId } = useBasketContext();
  const { data: basketData, loading } = useQuery(getBundleBasket, {
    variables: { basketId },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (basketData) {
      const basket = basketData?.basket as BundleBasketFragment;
      const supplyAddress = basket?.details?.supplyAddress;
      setFieldValue('ddBillingAddress', getAddressFieldValues(supplyAddress));
    }
  }, [basketData, setFieldValue]);

  const { aboutNameFirst, aboutNameLast } = values as FormValues &
    Record<string, string>;
  const aboutYouName =
    aboutNameFirst && aboutNameLast ? `${aboutNameFirst} ${aboutNameLast}` : '';

  useEffect(() => {
    setFieldValue('ddName', aboutYouName);
  }, [aboutYouName, setFieldValue]);

  const basket = basketData?.basket as BundleBasketFragment;

  const getFieldProps = useFieldProps<FormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  return (
    <Flex direction="column" spacing="xl">
      <TextInput
        {...getFieldProps('ddName')}
        className="details__formInput"
        label="Account holder name"
        data-testid="ddName"
        required
      />
      <MaskedInput
        {...getFieldProps('ddSortCode')}
        className={clsx('details__formInput', 'mp-mask')}
        label="Sort code"
        placeholder="11-11-11"
        helperText="6 digits"
        mask="99-99-99"
        onChange={(value) => setFieldValue('ddSortCode', value)}
        autoComplete="off"
        inputMode="numeric"
        data-testid="ddSortCode"
        required
      />
      <MaskedInput
        {...getFieldProps('ddAccountNumber')}
        className={clsx('details__formInput', 'mp-mask')}
        label="Account number"
        placeholder="12345678"
        helperText="8 digits"
        mask="99999999"
        onChange={(value) => setFieldValue('ddAccountNumber', value)}
        autoComplete="off"
        inputMode="numeric"
        data-testid="ddAccountNumber"
        required
      />
      <RadioGroup
        {...getFieldProps('ddBillingSame')}
        label={`Is your billing address also ${formatAddress(
          basket?.details?.supplyAddress || {}
        )}?`}
        className="details__RadioGroup"
        direction="row"
        onValueChange={(val: string) => setFieldValue('ddBillingSame', val)}
      >
        <RadioTile
          data-testid="ddBillingSameYes"
          value={BooleanSelection.Yes}
          label="Yes"
          id="ddBillingSameYes"
        />
        <RadioTile
          data-testid="ddBillingSameNo"
          value={BooleanSelection.No}
          label="No"
          id="ddBillingSameNo"
        />
      </RadioGroup>
      {values.ddBillingSame === BooleanSelection.No && (
        <Flex direction="column" spacing="lg">
          <Heading size="sm">Billing address</Heading>
          <AddressForm
            className="details__formInput"
            name="ddBillingAddressForm"
            isLoading={loading}
            onChange={(selectedAddress) =>
              setFieldValue('ddBillingAddress', selectedAddress)
            }
          />
        </Flex>
      )}
      {basket?.willCreditCheck ? (
        <>
          <BodyText ref={noticeRef}>
            Like most utility companies, we’ll run a credit check to work out if
            you’ll need to pay us a deposit. This will leave a “footprint” on
            your credit file (see our{' '}
            <InlineLink
              href={EXTERNAL_LINKS.privacyPolicy}
              target="_blank"
              hideOpenIcon
            >
              Privacy Policy
            </InlineLink>
            ). By submitting your order, you agree to this check being run.
          </BodyText>

          <BodyText>
            You also agree that you are authorised to set up a Direct Debit for
            this account.
          </BodyText>
        </>
      ) : (
        <BodyText>
          By submitting your order, you agree that you are authorised to set up
          a Direct Debit for this account.
        </BodyText>
      )}

      <Accordion
        className="detailsDirectDebit__accordion"
        onValueChange={(expanded: string) => setAccordionExpanded(expanded)}
        value={accordionExpanded}
        type="single"
        collapsible
      >
        <AccordionItem value="ddGuarantee" title="Direct Debit Guarantee">
          <AccordionContent>
            <Flex direction="column" spacing="sm" asChild>
              <ul className="detailsDirectDebit__GuaranteeList">
                <GuaranteeListItem>
                  This Guarantee is offered by all banks and building societies
                  that accept instructions to pay Direct Debits.
                </GuaranteeListItem>
                <GuaranteeListItem>
                  If there are any changes to the amount, date or frequency of
                  your Direct Debit the Utility Warehouse will notify you 3
                  working days in advance of your account being debited or as
                  otherwise agreed. If you request the Utility Warehouse to
                  collect a payment, confirmation of the amount and date will be
                  given to you at the time of the request.
                </GuaranteeListItem>
                <GuaranteeListItem>
                  If an error is made in the payment of your Direct Debit, by
                  the Utility Warehouse or your bank or building society, you
                  are entitled to a full and immediate refund of the amount paid
                  from your bank or building society.
                </GuaranteeListItem>
                <GuaranteeListItem>
                  If you receive a refund you are not entitled to, you must pay
                  it back when the Utility Warehouse asks you to.
                </GuaranteeListItem>
                <GuaranteeListItem>
                  You can cancel a Direct Debit at any time by simply contacting
                  your bank or building society. Written confirmation may be
                  required. Please also notify us.
                </GuaranteeListItem>
              </ul>
            </Flex>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Flex>
  );
};

export { DirectDebit };
